<script setup>

import {computed, onMounted, onUnmounted, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useAppStore} from '@/stores/app';
import {useSessionStore} from '@/stores/session';
import {useUserLoginStore} from '@/stores/userLogin';
import {usePasswordChangeStore} from '@/stores/passwordChange';
import UserLogin from '@/components/UserLogin.vue';
import PasswordChange from '@/components/PasswordChange.vue';
import {isShopEmployee, isShopManager} from "@/common/utils";

const route = useRoute();
const router = useRouter();
const sessionStore = useSessionStore();
const appStore = useAppStore();
const userLoginStore = useUserLoginStore();
const passwordChangeStore = usePasswordChangeStore();

const drawer = ref(false);
const isMobile = ref(false);
const isAdmin = computed(() => sessionStore.profile && sessionStore.profile.admin)

const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
});

// --- Scroll to Top FAB logic ---
const mainContent = ref(null);
const isFabVisible = ref(false);
const handleScroll = (event) => {
  isFabVisible.value = event.target.scrollTop > 200;
};
const scrollToTop = () => {
  const element = mainContent.value?.$el;
  if (!element) return;

  const duration = 200; // 0.2 seconds
  const start = element.scrollTop;
  const startTime = performance.now();

  const animateScroll = (currentTime) => {
    const elapsedTime = currentTime - startTime;
    const progress = Math.min(elapsedTime / duration, 1);
    const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;

    element.scrollTop = start * (1 - ease);

    if (elapsedTime < duration) {
      requestAnimationFrame(animateScroll);
    } else {
      element.scrollTop = 0;
    }
  };

  requestAnimationFrame(animateScroll);
};
// ---

const showSnackBar = params => snackbar.value = {show: true, ...params};

const username = computed(() => {
  const profile = sessionStore.profile;
  if (profile.admin) return '관리자*';

  else if (isShopManager(profile.account))
    return `${profile.name} | 매장 관리자`;

  else if (isShopEmployee(profile.account))
    return `${profile.name} | 일반사용자`;
});

const showSnackbar = (message, color = 'success') => {
  snackbar.value = {
    show: true,
    message,
    color
  };
};

const lastSSEPingText = computed(() => {
  if (!appStore.lastSSEPingAt) return '';
  const ts = typeof appStore.lastSSEPingAt === 'number' ? appStore.lastSSEPingAt : Number(appStore.lastSSEPingAt);
  if (Number.isNaN(ts) || ts <= 0) return '';
  try {
    return new Date(ts).toLocaleString('ko-KR');
  } catch (_) {
    return '';
  }
});

const checkMobile = () => {
  isMobile.value = window.innerWidth < 960;
  if (!isMobile.value) {
    drawer.value = true;
  }
};

const onLogout = async () => {
  if (!confirm('로그아웃 하시겠습니까?')) return;
  await sessionStore.logout();
};

// 비밀번호 변경 다이얼로그 표시
const showPasswordChangeDialog = async () => {
  try {
    const result = await passwordChangeStore.show({
      title: '비밀번호 변경',
      message: '보안을 위해 현재 비밀번호를 입력해주세요.',
      cancelText: '취소',
      okText: '변경'
    });

    if (result) {
      await handlePasswordChange(result);
    }
  } catch (error) {
    console.error('비밀번호 변경 팝업 오류:', error);
  }
};

// 비밀번호 변경 처리
const handlePasswordChange = async passwordData => {
  passwordChangeStore.isLoading = true;

  try {
    // 실제 비밀번호 변경 API 호출
    const [message, response] = await sessionStore.changePassword({
      password: passwordData.password,
      newPassword: passwordData.newPassword
    });

    if (message) {
      showSnackbar(message, 'error');
      return;
    }

    // 비밀번호 변경 성공
    showSnackbar('비밀번호가 성공적으로 변경되었습니다.', 'success');

  } catch (error) {
    console.error('비밀번호 변경 중 오류:', error);
    showSnackbar('비밀번호 변경 중 오류가 발생했습니다. 다시 시도해주세요.', 'error');
  } finally {
    passwordChangeStore.isLoading = false;
  }
};

const showManagerLogin = async () => {
  try {
    const result = await userLoginStore.show();

    if (result) {
      await handleUserLogin(result);
    }
  } catch (error) {
    console.error('로그인 팝업 오류:', error);
  }
};

// 사용자 로그인 처리
const handleUserLogin = async (loginData) => {
  if (!loginData.name.trim() || !loginData.password.trim()) {
    showSnackbar('이름과 비밀번호를 모두 입력해주세요.', 'error');
    return;
  }

  userLoginStore.isLogging = true;

  try {
    // 실제 사용자 로그인 API 호출
    const [message, response] = await sessionStore.loginUser({
      name: loginData.name,
      password: loginData.password
    });

    if (message) {
      showSnackbar(message, 'error');
      return;
    }

    // 로그인 성공
    sessionStore.profile = response.response;
    sessionStore.authorized = true;
    appStore.clear();
    showSnackbar(`${username.value} 님이 로그인 하었습니다.`, 'success');
    await appStore.loadShops();

  } catch (error) {
    console.error('사용자 로그인 중 오류:', error);
    showSnackbar('로그인 중 오류가 발생했습니다. 다시 시도해주세요.', 'error');
  } finally {
    userLoginStore.isLogging = false;
  }
};

onMounted(() => {
  console.log(sessionStore.profile);
  checkMobile();
  window.addEventListener('resize', checkMobile);
  appStore.connect();
});

onUnmounted(() => {
  appStore.disconnect();
  window.removeEventListener('resize', checkMobile);
});
</script>

<template>
  <!-- 사이드 메뉴 -->
  <v-navigation-drawer v-model="drawer" :permanent="!isMobile" app>
    <v-list>
      <v-list-item prepend-icon="mdi-home" title="홈" :to="{ name: 'home' }"></v-list-item>
      <v-list-item prepend-icon="mdi-monitor-eye" title="관제요청" :to="{ name: 'controlBoard' }"></v-list-item>
      <v-list-item prepend-icon="mdi-air-conditioner" title="냉난방기" :to="{name: 'devices'}"></v-list-item>
      <v-list-item prepend-icon="mdi-remote-tv" title="리모콘" :to="{name: 'remocons'}" v-if="isAdmin" />
<!--      <v-list-item prepend-icon="mdi-play-circle" title="프로그램" :to="{name: 'programs'}" v-if="isAdmin" />-->
      <v-list-item prepend-icon="mdi-history" title="운영 기록" :to="{name: 'logs'}"></v-list-item>
      <v-list-item prepend-icon="mdi-store" title="업체관리" :to="{ name: 'shops' }"></v-list-item>
<!--      <v-list-item prepend-icon="mdi-note-outline" title="메모" :to="{name: 'posts'}" v-if=isAdmin" />-->
      <v-list-item prepend-icon="mdi-cog-outline" title="설정" :to="{name: 'settings'}" v-if="isAdmin" />
    </v-list>

    <!-- PC에서만 로그아웃 버튼 표시 (우측 하단) -->
    <template v-if="!isMobile" v-slot:append>
      <div class="pa-2">
        <div class="d-flex justify-end align-center">
          <!-- SSE 연결 상태 표시 -->
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                :color="appStore.isSSEConnected ? 'success' : 'error'"
                size="small"
                class="mr-2"
              >
                {{ appStore.isSSEConnected ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
              </v-icon>
            </template>
            <span>Last Ping: {{ lastSSEPingText || '-' }}</span>
          </v-tooltip>

          <!-- 운영자나 일반사용자인 경우 샵이름과 사용자이름 표시 -->
          <v-menu v-if="username && sessionStore.profile.admin" location="bottom">
            <template v-slot:activator="{ props }">
              <div
                  class="flex-1-1 align-self-center pa-2 text-caption text-grey-7 user-info-text"
                  v-bind="props"
              >
                {{ username }}
              </div>
            </template>

            <v-list density="compact">
              <v-list-item @click="showPasswordChangeDialog">
                <template v-slot:prepend>
                  <v-icon size="small">mdi-lock-reset</v-icon>
                </template>
                <v-list-item-title>비밀번호 변경</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- 관리자가 아닌 경우 클릭 불가능한 텍스트 -->
          <div
              v-else-if="username"
              class="flex-1-1 align-self-center pa-2 text-caption text-grey-7"
          >
            {{ username }}
          </div>
          <!-- admin이 아니고, (샵사용자가 아니거나 QR사용자)이고, manager가 아닌 경우만 관리자 로그인 아이콘 표시 -->
          <v-btn size="small" variant="text" icon @click="showManagerLogin" v-else>
            <v-icon size="small">mdi-account-key</v-icon>
          </v-btn>
          <v-btn class="ml-2" size="small" variant="text" icon @click="onLogout">
            <v-icon size="small">mdi-logout</v-icon>
          </v-btn>
        </div>
      </div>
    </template>
  </v-navigation-drawer>

  <!-- 모바일에서만 상단 앱바 표시 -->
  <v-app-bar v-if="isMobile" app>
    <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
    <v-toolbar-title>KURC</v-toolbar-title>
    <v-spacer></v-spacer>

    <!-- SSE 연결 상태 표시 (모바일: 탭 시 스낵바 알림) -->
    <v-btn
      icon
      variant="text"
      @click="showSnackBar({ message: `Last Ping: ${lastSSEPingText || '-'}`, color: 'info' })"
    >
      <v-icon
        :color="appStore.isSSEConnected ? 'success' : 'error'"
        size="small"
        class="mr-2"
      >
        {{ appStore.isSSEConnected ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
      </v-icon>
    </v-btn>

    <!-- 운영자나 일반사용자인 경우 샵이름과 사용자이름 표시 -->
    <v-menu v-if="username && sessionStore.profile.admin" location="bottom">
      <template v-slot:activator="{ props }">
        <div
            class="text-center mr-2"
            v-bind="props"
        >
          <div
              class="text-caption text-grey-7 user-info-text"
              style="font-size: 10px;"
          >
            {{ username }}
          </div>
        </div>
      </template>

      <v-list density="compact">
        <v-list-item @click="showPasswordChangeDialog">
          <template v-slot:prepend>
            <v-icon size="small">mdi-lock-reset</v-icon>
          </template>
          <v-list-item-title>비밀번호 변경</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- 관리자가 아닌 경우 클릭 불가능한 텍스트 -->
    <div class="text-center mr-2" v-else-if="username">
      <div class="text-caption text-grey-7" style="font-size: 10px;">
        {{ username }}
      </div>
    </div>

    <!-- admin이 아니고, (샵사용자가 아니거나 QR사용자)이고, manager가 아닌 경우만 관리자 로그인 아이콘 표시 -->
    <v-btn icon @click="showManagerLogin" v-else>
      <v-icon>mdi-account-key</v-icon>
    </v-btn>

    <v-btn icon @click="onLogout">
      <v-icon>mdi-logout</v-icon>
    </v-btn>
  </v-app-bar>

  <!-- 메인 콘텐츠 -->
  <v-main ref="mainContent" class="main-container" @scroll="handleScroll">
    <router-view :key="route.fullPath" v-slot="{Component}">
      <component :is="Component" @showSnackBar="showSnackBar"/>
    </router-view>

    <v-btn
        v-if="isFabVisible"
        location="bottom right"
        position="fixed"
        icon="mdi-arrow-up"
        size="small"
        color="white"
        elevation="4"
        @click="scrollToTop"
        class="ma-4"
    ></v-btn>
  </v-main>

  <!-- 사용자 로그인 팝업 -->
  <UserLogin />

  <!-- 비밀번호 변경 팝업 -->
  <PasswordChange />

  <!-- Snackbar for notifications -->
  <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
  >
    {{ snackbar.message }}
    <template v-slot:actions>
      <v-btn
          color="white"
          variant="text"
          @click="snackbar.show = false"
      >
        닫기
      </v-btn>
    </template>
  </v-snackbar>
</template>

<style lang="scss" scoped>

.main-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  overflow-y: auto;
}

.user-info-text {
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}
</style>
